/**
 * EnrollmentsPage
 * Página de gestión de enrollments pendientes y rechazados
 */

import { useState } from 'react';
import { isAxiosError } from 'axios';
import { EnrollmentTokenDialog } from '@/components/enrollments/EnrollmentTokenDialog';
import { Box, Typography, Paper, Alert, Badge, Button, Collapse, Divider } from '@mui/material';
import {
  Block as BlockIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import {
  useEnrollments,
  useRejectedEnrollments,
  useApproveEnrollment,
  useDeleteEnrollment,
} from '@/hooks/useEnrollments';
import { useEnrollmentsStore } from '@/store/enrollmentsStore';
import { EnrollmentList } from '@/components/enrollments/EnrollmentList';
import PageHeader from '@/components/common/PageHeader';

export const EnrollmentsPage: React.FC = () => {
  const [approvingId, setApprovingId] = useState<string | undefined>();
  const [deletingId, setDeletingId] = useState<string | undefined>();
  const [notice, setNotice] = useState<string | undefined>();
  const [showRejected, setShowRejected] = useState(false);
  const [tokenOpen, setTokenOpen] = useState(false);
  const [actionError, setActionError] = useState<string>();

  const { data: enrollments, isLoading, error } = useEnrollments();
  const { isLoading: rejectedLoading } = useRejectedEnrollments();
  const approveEnrollment = useApproveEnrollment();
  const deleteEnrollment = useDeleteEnrollment();
  const { enrollments: storeEnrollments, rejected: storeRejected } = useEnrollmentsStore();

  const handleApprove = async (enrollmentId: string) => {
    if (confirm(`¿Querés aprobar el enrollment ${enrollmentId}?`)) {
      setApprovingId(enrollmentId);
      setActionError(undefined);
      setNotice(undefined);
      try {
        const updated = await approveEnrollment.mutateAsync(enrollmentId);
        if (updated?.status === 'approved') {
          setNotice('Equipo aprobado. Se conectará cuando complete la solicitud.');
        }
      } catch (error) {
        const code = isAxiosError(error)
          ? error.response?.data?.error
          : error instanceof Error
            ? error.message
            : undefined;
        setActionError(
          code === 'agent_quota_exceeded'
            ? 'La organización alcanzó el límite de equipos de su plan.'
            : code === 'access_denied'
              ? 'Ya no tenés permiso para aprobar esta solicitud.'
              : 'No se pudo aprobar el equipo. Intentá nuevamente.'
        );
      } finally {
        setApprovingId(undefined);
      }
    }
  };

  const handleDelete = async (enrollmentId: string) => {
    if (confirm(`¿Querés eliminar el enrollment ${enrollmentId}?`)) {
      setDeletingId(enrollmentId);
      setActionError(undefined);
      setNotice(undefined);
      try {
        await deleteEnrollment.mutateAsync(enrollmentId);
      } catch (error) {
        setActionError(
          (isAxiosError(error) && error.response?.status === 403) ||
            (error instanceof Error && error.message === 'access_denied')
            ? 'Ya no tenés permiso para rechazar esta solicitud.'
            : 'No se pudo rechazar el equipo. Intentá nuevamente.'
        );
      } finally {
        setDeletingId(undefined);
      }
    }
  };

  const displayEnrollments = storeEnrollments.length > 0 ? storeEnrollments : enrollments || [];
  const count = displayEnrollments.length;
  const rejectedCount = storeRejected.length;

  return (
    // Fill remaining viewport height (set up by MainLayout's flex column <main>)
    // so the lists scroll internally and the body never does. `flex: 1` +
    // `minHeight: 0` lets this Box shrink below its content size — without
    // `minHeight: 0` flex items default to `min-height: auto` and ignore
    // `overflow: auto` on their children.
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Pinned header — notice + page title + warning banner. Never scrolls. */}
      <Box sx={{ flexShrink: 0 }}>
        {notice && (
          <Box mb={2}>
            <Alert severity="info">{notice}</Alert>
          </Box>
        )}

        <PageHeader
          icon={<PersonAddIcon />}
          title={
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
              Enrollments
              {count > 0 && (
                <Badge badgeContent={count} color="warning" max={99}>
                  <Box sx={{ width: 8 }} />
                </Badge>
              )}
            </Box>
          }
          subtitle="Aprobá o rechazá los equipos que piden unirse a la flota."
          actions={
            <Button variant="contained" onClick={() => setTokenOpen(true)}>
              Agregar equipo
            </Button>
          }
        />

        {actionError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {actionError}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Error cargando enrollments:{' '}
            {error instanceof Error ? error.message : 'Error desconocido'}
          </Alert>
        )}

        {count > 0 && (
          <Paper sx={{ p: 2, mb: 2, bgcolor: 'warning.light', color: 'warning.contrastText' }}>
            <Typography variant="body1">
              Hay <strong>{count}</strong> enrollment{count !== 1 ? 's' : ''} pendiente
              {count !== 1 ? 's' : ''} de aprobación.
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              Revisá la información de cada dispositivo antes de aprobar.
            </Typography>
          </Paper>
        )}
      </Box>

      {tokenOpen && <EnrollmentTokenDialog onClose={() => setTokenOpen(false)} />}
      {/* Scrollable region — pending list + rejected section live here so the
          body itself never grows a scrollbar. `pr: 1` keeps scrollbar from
          sitting on top of card content. */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: 1 }}>
        <EnrollmentList
          enrollments={displayEnrollments}
          loading={isLoading}
          onApprove={handleApprove}
          onDelete={handleDelete}
          approvingId={approvingId}
          deletingId={deletingId}
        />

        <Divider sx={{ my: 4 }} />

        <Box>
          <Button
            variant="text"
            color="inherit"
            startIcon={<BlockIcon color={rejectedCount > 0 ? 'error' : 'disabled'} />}
            endIcon={showRejected ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            onClick={() => setShowRejected((v) => !v)}
            sx={{ textTransform: 'none' }}
          >
            <Typography variant="h6" component="span">
              Rechazados{rejectedCount > 0 ? ` (${rejectedCount})` : ''}
            </Typography>
          </Button>

          <Collapse in={showRejected}>
            <Box mt={2}>
              <Paper sx={{ p: 2, mb: 2, bgcolor: 'action.hover' }}>
                <Typography variant="body2" color="text.secondary">
                  Equipos que rechazaste. Para volver a agregar un equipo a una organización, generá
                  un código nuevo y comenzá una instalación nueva.
                </Typography>
              </Paper>
              <EnrollmentList
                enrollments={storeRejected}
                loading={rejectedLoading}
                variant="rejected"
                onApprove={handleApprove}
                approvingId={approvingId}
                emptyMessage="No hay equipos rechazados"
              />
            </Box>
          </Collapse>
        </Box>
      </Box>
    </Box>
  );
};
